/**
 * Painel de Anotações do Usuário
 * Permite criar, editar e gerenciar anotações, destaques e favoritos em segmentos
 */

import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Alert,
  Tooltip,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import HighlightIcon from '@mui/icons-material/Highlight'
import NoteIcon from '@mui/icons-material/Note'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LinkIcon from '@mui/icons-material/Link'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { 
  Annotation, 
  SegmentHighlight, 
  Bookmark, 
  UserAnnotations,
  AnnotationColor,
  AnnotationCategory,
  Transcription
} from '../../../types/transcription'
import { getAnnotations, updateAnnotations } from '../../../services/api'

type Props = {
  fileId: string
  transcription?: Transcription
  onNavigateToSegment?: (segmentIndex: number) => void
  // Props para estado compartilhado (opcional)
  annotations?: UserAnnotations
  onAnnotationsChange?: (annotations: UserAnnotations) => void
  /** Modo compacto para uso no painel lateral */
  compact?: boolean
}

export type AnnotationsPanelHandle = {
  openAddNote: (segmentIndex?: number, text?: string) => void
  openAddHighlight: (segmentIndex?: number, color?: AnnotationColor) => void
  openAddBookmark: (segmentIndex?: number) => void
}

type SubTabValue = 'annotations' | 'highlights' | 'bookmarks'

const COLORS: { value: AnnotationColor; label: string; hex: string }[] = [
  { value: 'yellow', label: 'Amarelo', hex: '#fff59d' },
  { value: 'green', label: 'Verde', hex: '#a5d6a7' },
  { value: 'blue', label: 'Azul', hex: '#90caf9' },
  { value: 'red', label: 'Vermelho', hex: '#ef9a9a' },
  { value: 'purple', label: 'Roxo', hex: '#ce93d8' }
]

const CATEGORIES: { value: AnnotationCategory; label: string; icon: React.ReactNode }[] = [
  { value: 'note', label: 'Nota', icon: <NoteIcon fontSize="small" /> },
  { value: 'question', label: 'Dúvida', icon: <QuestionMarkIcon fontSize="small" /> },
  { value: 'important', label: 'Importante', icon: <PriorityHighIcon fontSize="small" /> },
  { value: 'todo', label: 'A Fazer', icon: <CheckCircleOutlineIcon fontSize="small" /> },
  { value: 'reference', label: 'Referência', icon: <LinkIcon fontSize="small" /> }
]

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

function formatTime(timeStr: string | number | undefined | null): string {
  if (timeStr === null || timeStr === undefined) return '00:00'
  
  // Se for número, converte para string no formato MM:SS
  if (typeof timeStr === 'number') {
    const totalSeconds = Math.floor(timeStr)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  
  // Se não for string, tenta converter
  const str = String(timeStr)
  if (!str) return '00:00'
  
  const parts = str.split(':')
  if (parts.length === 3) {
    return `${parts[0]}:${parts[1]}:${parts[2].split('.')[0]}`
  }
  if (parts.length === 2) {
    return `${parts[0]}:${parts[1].split('.')[0]}`
  }
  const totalSeconds = Math.floor(parseFloat(str))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const AnnotationsPanel = forwardRef<AnnotationsPanelHandle, Props>(function AnnotationsPanel({
  fileId,
  transcription,
  onNavigateToSegment,
  annotations: externalAnnotations,
  onAnnotationsChange,
  compact = false,
}: Props, ref) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [subTab, setSubTab] = useState<SubTabValue>('annotations')
  const [internalData, setInternalData] = useState<UserAnnotations>({ items: [], highlights: [], bookmarks: [] })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Usar dados externos se disponíveis, caso contrário usar internos
  const isExternallyControlled = externalAnnotations !== undefined && onAnnotationsChange !== undefined
  const data = isExternallyControlled ? externalAnnotations : internalData
  
  // Modal states
  const [annotationDialogOpen, setAnnotationDialogOpen] = useState(false)
  const [highlightDialogOpen, setHighlightDialogOpen] = useState(false)
  const [bookmarkDialogOpen, setBookmarkDialogOpen] = useState(false)
  
  // Edit item state
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null)
  const [editingHighlight, setEditingHighlight] = useState<SegmentHighlight | null>(null)
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null)
  
  // Form states
  const [formSegmentIndex, setFormSegmentIndex] = useState<number>(0)
  const [formText, setFormText] = useState('')
  const [formColor, setFormColor] = useState<AnnotationColor>('yellow')
  const [formCategory, setFormCategory] = useState<AnnotationCategory>('note')
  const [formLabel, setFormLabel] = useState('')
  const [formTitle, setFormTitle] = useState('')
  const [formDescription, setFormDescription] = useState('')

  const segments = transcription?.transcript || []

  // Imperative handle para controle externo (ex: menu de contexto)
  useImperativeHandle(ref, () => ({
    openAddNote: (segmentIndex = 0, text = '') => {
      setEditingAnnotation(null)
      setFormSegmentIndex(segmentIndex)
      setFormText(text)
      setFormColor('yellow')
      setFormCategory('note')
      setSubTab('annotations')
      setAnnotationDialogOpen(true)
    },
    openAddHighlight: (segmentIndex = 0, color) => {
      setEditingHighlight(null)
      setFormSegmentIndex(segmentIndex)
      setFormColor(color || 'yellow')
      setFormLabel('')
      setSubTab('highlights')
      setHighlightDialogOpen(true)
    },
    openAddBookmark: (segmentIndex = 0) => {
      setEditingBookmark(null)
      setFormSegmentIndex(segmentIndex)
      setFormTitle('')
      setFormDescription('')
      setSubTab('bookmarks')
      setBookmarkDialogOpen(true)
    },
  }), [])

  // Load annotations on mount (only if not externally controlled)
  useEffect(() => {
    if (!isExternallyControlled) {
      loadAnnotations()
    } else {
      setLoading(false)
    }
  }, [fileId, isExternallyControlled])

  const loadAnnotations = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getAnnotations(fileId)
      setInternalData(result)
    } catch (e) {
      console.error('Erro ao carregar anotações:', e)
      setError('Não foi possível carregar as anotações.')
    } finally {
      setLoading(false)
    }
  }

  const saveAnnotations = async (newData: UserAnnotations) => {
    // Se controlado externamente, apenas notifica a mudança
    if (isExternallyControlled) {
      onAnnotationsChange(newData)
      return
    }
    
    // Caso contrário, salva diretamente
    setSaving(true)
    try {
      await updateAnnotations(fileId, newData)
      setInternalData(newData)
    } catch (e) {
      console.error('Erro ao salvar anotações:', e)
      setError('Erro ao salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  // Reset forms
  const resetAnnotationForm = () => {
    setFormSegmentIndex(0)
    setFormText('')
    setFormColor('yellow')
    setFormCategory('note')
    setEditingAnnotation(null)
  }

  const resetHighlightForm = () => {
    setFormSegmentIndex(0)
    setFormColor('yellow')
    setFormLabel('')
    setEditingHighlight(null)
  }

  const resetBookmarkForm = () => {
    setFormSegmentIndex(0)
    setFormTitle('')
    setFormDescription('')
    setEditingBookmark(null)
  }

  // Annotation handlers
  const handleOpenAnnotationDialog = (annotation?: Annotation) => {
    if (annotation) {
      setEditingAnnotation(annotation)
      setFormSegmentIndex(annotation.segmentIndex)
      setFormText(annotation.text)
      setFormColor(annotation.color)
      setFormCategory(annotation.category)
    } else {
      resetAnnotationForm()
    }
    setAnnotationDialogOpen(true)
  }

  const handleSaveAnnotation = async () => {
    const segment = segments[formSegmentIndex]
    const now = Date.now()
    
    if (editingAnnotation) {
      const updated: Annotation = {
        ...editingAnnotation,
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        text: formText,
        segmentText: segment?.text?.substring(0, 100) || '',
        color: formColor,
        category: formCategory,
        updatedAt: now
      }
      const newItems = data.items.map(a => a.id === updated.id ? updated : a)
      await saveAnnotations({ ...data, items: newItems })
    } else {
      const newAnnotation: Annotation = {
        id: generateId(),
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        text: formText,
        segmentText: segment?.text?.substring(0, 100) || '',
        color: formColor,
        category: formCategory,
        createdAt: now,
        updatedAt: now
      }
      await saveAnnotations({ ...data, items: [...data.items, newAnnotation] })
    }
    
    setAnnotationDialogOpen(false)
    resetAnnotationForm()
  }

  const handleDeleteAnnotation = async (id: string) => {
    const newItems = data.items.filter(a => a.id !== id)
    await saveAnnotations({ ...data, items: newItems })
  }

  // Highlight handlers
  const handleOpenHighlightDialog = (highlight?: SegmentHighlight) => {
    if (highlight) {
      setEditingHighlight(highlight)
      setFormSegmentIndex(highlight.segmentIndex)
      setFormColor(highlight.color)
      setFormLabel(highlight.label || '')
    } else {
      resetHighlightForm()
    }
    setHighlightDialogOpen(true)
  }

  const handleSaveHighlight = async () => {
    const segment = segments[formSegmentIndex]
    const now = Date.now()
    
    if (editingHighlight) {
      const updated: SegmentHighlight = {
        ...editingHighlight,
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        color: formColor,
        label: formLabel || undefined,
        createdAt: editingHighlight.createdAt
      }
      const newHighlights = data.highlights.map(h => h.id === updated.id ? updated : h)
      await saveAnnotations({ ...data, highlights: newHighlights })
    } else {
      const newHighlight: SegmentHighlight = {
        id: generateId(),
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        color: formColor,
        label: formLabel || undefined,
        createdAt: now
      }
      await saveAnnotations({ ...data, highlights: [...data.highlights, newHighlight] })
    }
    
    setHighlightDialogOpen(false)
    resetHighlightForm()
  }

  const handleDeleteHighlight = async (id: string) => {
    const newHighlights = data.highlights.filter(h => h.id !== id)
    await saveAnnotations({ ...data, highlights: newHighlights })
  }

  // Bookmark handlers
  const handleOpenBookmarkDialog = (bookmark?: Bookmark) => {
    if (bookmark) {
      setEditingBookmark(bookmark)
      setFormSegmentIndex(bookmark.segmentIndex)
      setFormTitle(bookmark.title)
      setFormDescription(bookmark.description || '')
    } else {
      resetBookmarkForm()
    }
    setBookmarkDialogOpen(true)
  }

  const handleSaveBookmark = async () => {
    const segment = segments[formSegmentIndex]
    const now = Date.now()
    
    if (editingBookmark) {
      const updated: Bookmark = {
        ...editingBookmark,
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        title: formTitle,
        description: formDescription || undefined
      }
      const newBookmarks = data.bookmarks.map(b => b.id === updated.id ? updated : b)
      await saveAnnotations({ ...data, bookmarks: newBookmarks })
    } else {
      const newBookmark: Bookmark = {
        id: generateId(),
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        title: formTitle,
        description: formDescription || undefined,
        createdAt: now
      }
      await saveAnnotations({ ...data, bookmarks: [...data.bookmarks, newBookmark] })
    }
    
    setBookmarkDialogOpen(false)
    resetBookmarkForm()
  }

  const handleDeleteBookmark = async (id: string) => {
    const newBookmarks = data.bookmarks.filter(b => b.id !== id)
    await saveAnnotations({ ...data, bookmarks: newBookmarks })
  }

  // Navigation
  const handleNavigate = (segmentIndex: number) => {
    if (onNavigateToSegment) {
      onNavigateToSegment(segmentIndex)
    }
  }

  const getCategoryIcon = (category: AnnotationCategory) => {
    return CATEGORIES.find(c => c.value === category)?.icon || <NoteIcon fontSize="small" />
  }

  const getColorHex = (color: AnnotationColor) => {
    return COLORS.find(c => c.value === color)?.hex || '#fff59d'
  }

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid #e0e0e0' }}>
        <Skeleton variant="text" width="40%" height={isMobile ? 24 : 32} />
        <Box sx={{ mt: 2 }}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={isMobile ? 50 : 60} sx={{ mb: 1 }} />
          ))}
        </Box>
      </Paper>
    )
  }

  return (
    <Paper elevation={0} sx={{ border: compact ? 'none' : '1px solid #e0e0e0' }}>
      {/* Header - oculto no modo compacto (o ToolsSidePanel já exibe título) */}
      {!compact && (
        <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
            📝 {isMobile ? 'Anotações' : 'Anotações'}
            {saving && <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>(salvando...)</Typography>}
          </Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              Crie notas, destaques e favoritos para organizar sua análise
            </Typography>
          )}
        </Box>
      )}
      {compact && saving && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5, display: 'block', textAlign: 'right', fontSize: '0.65rem' }}>
          salvando...
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Sub-tabs */}
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{
          px: { xs: 1, sm: compact ? 0 : 2 },
          borderBottom: '1px solid #e0e0e0',
          minHeight: compact ? 34 : undefined,
          '& .MuiTab-root': compact ? { minHeight: 34, py: 0.5, textTransform: 'none', fontSize: '0.75rem', fontWeight: 500 } : {},
        }}
        variant={isMobile || compact ? 'fullWidth' : 'standard'}
      >
        <Tab
          value="annotations"
          sx={{ minWidth: 'auto', fontSize: { xs: '0.7rem', sm: compact ? '0.75rem' : '0.875rem' }, px: { xs: 1, sm: compact ? 0.5 : 2 } }}
          label={
            compact ? (
              <span>Notas{data.items.length > 0 ? ` (${data.items.length})` : ''}</span>
            ) : (
              <Badge badgeContent={data.items.length} color="primary" max={99}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <NoteIcon sx={{ fontSize: { xs: 16, sm: 20 } }} /> Notas
                </Box>
              </Badge>
            )
          }
        />
        <Tab
          value="highlights"
          sx={{ minWidth: 'auto', fontSize: { xs: '0.7rem', sm: compact ? '0.75rem' : '0.875rem' }, px: { xs: 1, sm: compact ? 0.5 : 2 } }}
          label={
            compact ? (
              <span>Destaques{data.highlights.length > 0 ? ` (${data.highlights.length})` : ''}</span>
            ) : (
              <Badge badgeContent={data.highlights.length} color="secondary" max={99}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <HighlightIcon sx={{ fontSize: { xs: 16, sm: 20 } }} /> Destaques
                </Box>
              </Badge>
            )
          }
        />
        <Tab
          value="bookmarks"
          sx={{ minWidth: 'auto', fontSize: { xs: '0.7rem', sm: compact ? '0.75rem' : '0.875rem' }, px: { xs: 1, sm: compact ? 0.5 : 2 } }}
          label={
            compact ? (
              <span>Favoritos{data.bookmarks.length > 0 ? ` (${data.bookmarks.length})` : ''}</span>
            ) : (
              <Badge badgeContent={data.bookmarks.length} color="info" max={99}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BookmarkIcon sx={{ fontSize: { xs: 16, sm: 20 } }} /> Favoritos
                </Box>
              </Badge>
            )
          }
        />
      </Tabs>

      <Box sx={{ p: compact ? 1 : { xs: 1.5, sm: 2 } }}>
        {/* Annotations Tab */}
        {subTab === 'annotations' && (
          <>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAnnotationDialog()}
              sx={{ mb: compact ? 1 : { xs: 1.5, sm: 2 }, fontSize: compact ? '0.75rem' : { xs: '0.75rem', sm: '0.875rem' } }}
              disabled={segments.length === 0}
              size="small"
              fullWidth={compact}
            >
              + Nova Nota
            </Button>

            {data.items.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: compact ? 2 : { xs: 3, sm: 4 }, color: 'text.secondary' }}>
                <NoteIcon sx={{ fontSize: compact ? 32 : { xs: 36, sm: 48 }, opacity: 0.3 }} />
                <Typography sx={{ fontSize: compact ? '0.8rem' : { xs: '0.85rem', sm: '1rem' } }}>Nenhuma nota criada</Typography>
                <Typography variant="body2" sx={{ fontSize: compact ? '0.7rem' : { xs: '0.75rem', sm: '0.875rem' } }}>
                  Use o menu de contexto ou clique acima
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {data.items.sort((a, b) => a.segmentIndex - b.segmentIndex).map((annotation) => (
                  <ListItem
                    key={annotation.id}
                    sx={{
                      mb: compact ? 0.5 : { xs: 0.75, sm: 1 },
                      bgcolor: getColorHex(annotation.color),
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.1)',
                      p: compact ? 1 : { xs: 1, sm: 2 },
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      position: 'relative',
                    }}
                  >
                    {/* Actions row — top right */}
                    <Box sx={{
                      position: 'absolute',
                      top: compact ? 4 : 6,
                      right: compact ? 2 : 4,
                      display: 'flex',
                      gap: 0,
                    }}>
                      <Tooltip title="Ir para segmento">
                        <IconButton size="small" onClick={() => handleNavigate(annotation.segmentIndex)} sx={{ p: 0.25 }}>
                          <NavigateNextIcon sx={{ fontSize: compact ? 16 : 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenAnnotationDialog(annotation)} sx={{ p: 0.25 }}>
                          <EditIcon sx={{ fontSize: compact ? 14 : 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDeleteAnnotation(annotation.id)} color="error" sx={{ p: 0.25 }}>
                          <DeleteIcon sx={{ fontSize: compact ? 14 : 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Content */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: compact ? 7 : 8, width: '100%' }}>
                      {getCategoryIcon(annotation.category)}
                      <Typography variant="body2" sx={{
                        fontWeight: 500,
                        fontSize: compact ? '0.78rem' : { xs: '0.8rem', sm: '0.875rem' },
                        display: '-webkit-box',
                        WebkitLineClamp: compact ? 2 : (isMobile ? 2 : 10),
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                      }}>
                        {annotation.text}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: compact ? '0.6rem' : '0.7rem', lineHeight: 1.3 }}>
                      📍 Seg. {annotation.segmentIndex + 1} • {formatTime(annotation.startTime)}
                      {!compact && annotation.segmentText && <> — <em>"{annotation.segmentText.substring(0, 40)}..."</em></>}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? '0.55rem' : '0.65rem' }}>
                      {formatDate(annotation.createdAt)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {/* Highlights Tab */}
        {subTab === 'highlights' && (
          <>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenHighlightDialog()}
              sx={{ mb: compact ? 1 : { xs: 1.5, sm: 2 }, fontSize: compact ? '0.75rem' : { xs: '0.75rem', sm: '0.875rem' } }}
              disabled={segments.length === 0}
              size="small"
              fullWidth={compact}
            >
              + Novo Destaque
            </Button>

            {data.highlights.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: compact ? 2 : { xs: 3, sm: 4 }, color: 'text.secondary' }}>
                <HighlightIcon sx={{ fontSize: compact ? 32 : { xs: 36, sm: 48 }, opacity: 0.3 }} />
                <Typography sx={{ fontSize: compact ? '0.8rem' : { xs: '0.85rem', sm: '1rem' } }}>Nenhum destaque criado</Typography>
                <Typography variant="body2" sx={{ fontSize: compact ? '0.7rem' : { xs: '0.75rem', sm: '0.875rem' } }}>
                  Use o menu de contexto ou clique acima
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {data.highlights.sort((a, b) => a.segmentIndex - b.segmentIndex).map((highlight) => (
                  <ListItem
                    key={highlight.id}
                    sx={{
                      mb: compact ? 0.5 : { xs: 0.75, sm: 1 },
                      bgcolor: getColorHex(highlight.color),
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.1)',
                      p: compact ? 1 : { xs: 1, sm: 2 },
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      position: 'relative',
                    }}
                  >
                    {/* Actions row — top right */}
                    <Box sx={{
                      position: 'absolute',
                      top: compact ? 4 : 6,
                      right: compact ? 2 : 4,
                      display: 'flex',
                      gap: 0,
                    }}>
                      <Tooltip title="Ir para segmento">
                        <IconButton size="small" onClick={() => handleNavigate(highlight.segmentIndex)} sx={{ p: 0.25 }}>
                          <NavigateNextIcon sx={{ fontSize: compact ? 16 : 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenHighlightDialog(highlight)} sx={{ p: 0.25 }}>
                          <EditIcon sx={{ fontSize: compact ? 14 : 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDeleteHighlight(highlight.id)} color="error" sx={{ p: 0.25 }}>
                          <DeleteIcon sx={{ fontSize: compact ? 14 : 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Content */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: compact ? 7 : 8, width: '100%' }}>
                      <HighlightIcon sx={{ fontSize: compact ? 16 : 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: compact ? '0.78rem' : '0.875rem' }}>
                        Seg. {highlight.segmentIndex + 1}
                        {highlight.label && ` — ${highlight.label}`}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, fontSize: compact ? '0.6rem' : '0.7rem' }}>
                      ⏱️ {formatTime(highlight.startTime)} • {formatDate(highlight.createdAt)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {/* Bookmarks Tab */}
        {subTab === 'bookmarks' && (
          <>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenBookmarkDialog()}
              sx={{ mb: compact ? 1 : { xs: 1.5, sm: 2 }, fontSize: compact ? '0.75rem' : { xs: '0.75rem', sm: '0.875rem' } }}
              disabled={segments.length === 0}
              size="small"
              fullWidth={compact}
            >
              + Novo Favorito
            </Button>

            {data.bookmarks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: compact ? 2 : { xs: 3, sm: 4 }, color: 'text.secondary' }}>
                <BookmarkIcon sx={{ fontSize: compact ? 32 : { xs: 36, sm: 48 }, opacity: 0.3 }} />
                <Typography sx={{ fontSize: compact ? '0.8rem' : { xs: '0.85rem', sm: '1rem' } }}>Nenhum favorito criado</Typography>
                <Typography variant="body2" sx={{ fontSize: compact ? '0.7rem' : { xs: '0.75rem', sm: '0.875rem' } }}>
                  Use o menu de contexto ou clique acima
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {data.bookmarks.sort((a, b) => a.segmentIndex - b.segmentIndex).map((bookmark) => (
                  <ListItem
                    key={bookmark.id}
                    sx={{
                      mb: compact ? 0.5 : { xs: 0.75, sm: 1 },
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                      p: compact ? 1 : { xs: 1, sm: 2 },
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      position: 'relative',
                    }}
                  >
                    {/* Actions row — top right */}
                    <Box sx={{
                      position: 'absolute',
                      top: compact ? 4 : 6,
                      right: compact ? 2 : 4,
                      display: 'flex',
                      gap: 0,
                    }}>
                      <Tooltip title="Ir para segmento">
                        <IconButton size="small" onClick={() => handleNavigate(bookmark.segmentIndex)} sx={{ p: 0.25 }}>
                          <NavigateNextIcon sx={{ fontSize: compact ? 16 : 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenBookmarkDialog(bookmark)} sx={{ p: 0.25 }}>
                          <EditIcon sx={{ fontSize: compact ? 14 : 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDeleteBookmark(bookmark.id)} color="error" sx={{ p: 0.25 }}>
                          <DeleteIcon sx={{ fontSize: compact ? 14 : 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Content */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: compact ? 7 : 8, width: '100%' }}>
                      <BookmarkIcon sx={{ fontSize: compact ? 16 : 18 }} color="primary" />
                      <Typography variant="body2" sx={{
                        fontWeight: 500,
                        fontSize: compact ? '0.78rem' : '0.875rem',
                        display: '-webkit-box',
                        WebkitLineClamp: compact ? 1 : 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {bookmark.title}
                      </Typography>
                    </Box>
                    {bookmark.description && !compact && (
                      <Typography variant="caption" sx={{ mt: 0.25, fontSize: '0.7rem' }}>
                        {bookmark.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, fontSize: compact ? '0.6rem' : '0.7rem' }}>
                      📍 Seg. {bookmark.segmentIndex + 1} • {formatTime(bookmark.startTime)} • {formatDate(bookmark.createdAt)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </Box>

      {/* Annotation Dialog */}
      <Dialog open={annotationDialogOpen} onClose={() => setAnnotationDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, pb: { xs: 1, sm: 2 } }}>{editingAnnotation ? 'Editar Nota' : 'Nova Nota'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>Segmento</InputLabel>
              <Select
                value={formSegmentIndex}
                onChange={(e) => setFormSegmentIndex(Number(e.target.value))}
                label="Segmento"
                sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                {segments.map((seg, idx) => (
                  <MenuItem key={idx} value={idx} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                    {idx + 1}. [{formatTime(seg.start_time)}] {seg.text?.substring(0, isMobile ? 30 : 50)}...
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Texto da nota"
              multiline
              rows={isMobile ? 2 : 3}
              value={formText}
              onChange={(e) => setFormText(e.target.value)}
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              InputLabelProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
              inputProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
            />

            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>Categoria</InputLabel>
              <Select
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value as AnnotationCategory)}
                label="Categoria"
                sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {cat.icon} {cat.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Cor</Typography>
              <ToggleButtonGroup
                value={formColor}
                exclusive
                onChange={(_, val) => val && setFormColor(val)}
                size="small"
              >
                {COLORS.map((color) => (
                  <ToggleButton key={color.value} value={color.value} sx={{ p: { xs: 0.5, sm: 1 } }}>
                    <Box
                      sx={{
                        width: { xs: 18, sm: 20 },
                        height: { xs: 18, sm: 20 },
                        bgcolor: color.hex,
                        borderRadius: '50%',
                        border: '1px solid rgba(0,0,0,0.2)'
                      }}
                    />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button onClick={() => setAnnotationDialogOpen(false)} size={isMobile ? 'small' : 'medium'}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAnnotation} disabled={!formText.trim()} size={isMobile ? 'small' : 'medium'}>
            {editingAnnotation ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Highlight Dialog */}
      <Dialog open={highlightDialogOpen} onClose={() => setHighlightDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, pb: { xs: 1, sm: 2 } }}>{editingHighlight ? 'Editar Destaque' : 'Novo Destaque'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>Segmento</InputLabel>
              <Select
                value={formSegmentIndex}
                onChange={(e) => setFormSegmentIndex(Number(e.target.value))}
                label="Segmento"
                sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                {segments.map((seg, idx) => (
                  <MenuItem key={idx} value={idx} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                    {idx + 1}. [{formatTime(seg.start_time)}] {seg.text?.substring(0, isMobile ? 30 : 50)}...
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Rótulo (opcional)"
              value={formLabel}
              onChange={(e) => setFormLabel(e.target.value)}
              fullWidth
              placeholder="Ex: Trecho importante"
              size={isMobile ? 'small' : 'medium'}
              InputLabelProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
              inputProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Cor do destaque</Typography>
              <ToggleButtonGroup
                value={formColor}
                exclusive
                onChange={(_, val) => val && setFormColor(val)}
                size="small"
              >
                {COLORS.map((color) => (
                  <ToggleButton key={color.value} value={color.value} sx={{ p: { xs: 0.5, sm: 1 } }}>
                    <Box
                      sx={{
                        width: { xs: 18, sm: 20 },
                        height: { xs: 18, sm: 20 },
                        bgcolor: color.hex,
                        borderRadius: '50%',
                        border: '1px solid rgba(0,0,0,0.2)'
                      }}
                    />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button onClick={() => setHighlightDialogOpen(false)} size={isMobile ? 'small' : 'medium'}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveHighlight} size={isMobile ? 'small' : 'medium'}>
            {editingHighlight ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bookmark Dialog */}
      <Dialog open={bookmarkDialogOpen} onClose={() => setBookmarkDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, pb: { xs: 1, sm: 2 } }}>{editingBookmark ? 'Editar Favorito' : 'Novo Favorito'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>Segmento</InputLabel>
              <Select
                value={formSegmentIndex}
                onChange={(e) => setFormSegmentIndex(Number(e.target.value))}
                label="Segmento"
                sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                {segments.map((seg, idx) => (
                  <MenuItem key={idx} value={idx} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                    {idx + 1}. [{formatTime(seg.start_time)}] {seg.text?.substring(0, isMobile ? 30 : 50)}...
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Título"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              fullWidth
              placeholder={isMobile ? 'Ex: Momento crucial' : 'Ex: Momento crucial da entrevista'}
              size={isMobile ? 'small' : 'medium'}
              InputLabelProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
              inputProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
            />

            <TextField
              label="Descrição (opcional)"
              multiline
              rows={isMobile ? 2 : 2}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              InputLabelProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
              inputProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button onClick={() => setBookmarkDialogOpen(false)} size={isMobile ? 'small' : 'medium'}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveBookmark} disabled={!formTitle.trim()} size={isMobile ? 'small' : 'medium'}>
            {editingBookmark ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
})

export default AnnotationsPanel
